import React, {
  createContext,
  useCallback,
  useEffect,
  useReducer,
} from "react";
import { Auth } from "aws-amplify";

import api from "apiSingleton";

import { contextConstants } from "context/constants";
import { contextReducers } from "context/reducers";
import { contextActions } from "context/actions";
import { isAuthenticationError } from "utils/authErrorHandler";
import { isHeapReady } from "utils/heapAnalytics";
import moment from "moment";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    contextReducers.auth.reducer,
    contextReducers.auth.initialState
  );

  const handleSessionExpiration = useCallback(() => {
    dispatch({ type: contextConstants.auth.USER_SIGNEDOUT });
    Auth.signOut();
  }, [dispatch]);

  const initUser = useCallback(async () => {
    let user;
    try {
      user = await contextActions.auth.initUser();
    } catch (error) {
      if (isAuthenticationError(error)) {
        handleSessionExpiration();
        return;
      }
      throw error;
    }

    if (user?.id) {
      try {
        const { data } = await api.user.getMe();
        user = { ...user, ...data.getMe };
      } catch (error) {
        if (isAuthenticationError(error)) {
          handleSessionExpiration();
          return;
        }
        throw error;
      }
    }

    const groups = user?.attributes?.groups ?? [];
    user.canAccessApplication =
      groups.includes(process.env.REACT_APP_COGNITO_ADMIN_GROUP) ||
      groups.includes(process.env.REACT_APP_COGNITO_GROUP_USER_MANAGERS);

    user.canAccessJobAssistant = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_ALLOW_JOB_ASSISTANT
    );

    user.canFetchIdealCandidates = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_ALLOW_FETCH_IDEAL // I think I can add _CANDIDATES but I may hit length limit
    );

    user.canAccessMatchAgent = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_ALLOW_MATCH_AGENT
    );

    user.canAccessPreCandidates = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_ALLOW_PRECANDIDATES_TAB
    );

    user.canManuallyAcceptCandidates = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_MANUAL_ACCEPTANCE_ENABLED
    );

    user.canAccessResumeProfileTalentCreation = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_ALLOW_RESUME_PROFILE
    );

    user.canAccessTVA = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_ALLOW_TVA_ACCESS
    );

    user.canAccessTalentSourceFilter = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_ALLOW_TALENT_SOURCE_FILTER
    );

    user.canDownloadSearchResults = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_ALLOW_DOWNLOAD_RESULTS
    );

    user.canAccessBulkDownload = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_BULK_DOWNLOAD
    );

    user.canSendAssessments = groups.includes(
      process.env.REACT_APP_COGNITO_GROUP_ALLOW_SEND_ASSESSMENTS
    );

    const webApplyGroup = process.env.REACT_APP_COGNITO_GROUP_ALLOW_WEB_APPLYS;
    user.canAccessWebApplys = !webApplyGroup || groups.includes(webApplyGroup);

    user.canAccessCommunityBoost =
      process.env.REACT_APP_COMMUNITY_BOOST_FEATURE_ENABLED === "true" ||
      groups.includes(
        process.env.REACT_APP_COGNITO_GROUP_COMMUNITY_BOOST_ENABLED
      );

    const ALLOWED_GROUPS = JSON.parse(
      process.env.REACT_APP_ALLOWED_GROUPS_LIST_VIEW_ACCESS || "[]"
    );

    user.canAccessListView = !!groups.find((c) => ALLOWED_GROUPS.includes(c));

    if (
      process.env.REACT_APP_VERCEL_ENV !== "production" &&
      window.__testPermissionOverrides
    ) {
      Object.assign(user, window.__testPermissionOverrides);
    }

    dispatch({
      type: contextConstants.auth.USER_UPDATED,
      payload: { user },
    });
  }, [handleSessionExpiration]);

  const auth = {
    ...state,
    dispatch,
  };

  useEffect(() => {
    initUser();
  }, [initUser]);

  useEffect(() => {
    const isUserLoggedIn = !!auth.user?.attributes?.sub;
    if (isUserLoggedIn && isHeapReady("identify")) {
      window.heap.identify(auth.user.attributes.sub);
      window.heap.addUserProperties({
        username: auth.user.username,
        email: auth.user.email,
        logInDate: moment().format("YYYY-MM-DDTHH:mm:ss"),
      });
    }
  }, [auth.user?.attributes?.sub, auth.user?.username, auth.user?.email]);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
